import * as React from "react";
import { Button as BaseButton } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none border-2 border-transparent text-sm font-bold tracking-tight uppercase transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
  {
    variants: {
      variant: {
        default:
          "bg-black text-white border-black hover:bg-white hover:text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]",
        accent:
          "bg-[var(--accent-raw)] text-white border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-[var(--accent-hover)]",
        outline:
          "border-black bg-transparent text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-black hover:text-white hover:border-black",
        ghost:
          "bg-transparent text-black border-transparent hover:border-black hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]",
        destructive:
          "bg-[var(--error)] text-white border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-white hover:text-[var(--error)]",
        link: "text-black underline-offset-4 hover:underline uppercase font-bold text-xs bg-transparent border-none active:translate-x-0 active:translate-y-0",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-4 text-xs",
        lg: "h-12 px-8 text-base",
        icon: "h-10 w-10 p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const mergedClassName = cn(buttonVariants({ variant, size, className }));

    if (asChild && React.isValidElement(children)) {
      const renderElement = children as React.ReactElement;
      const isNativeButton =
        typeof renderElement.type === "string" && renderElement.type === "button";

      return (
        <BaseButton
          ref={ref as React.Ref<HTMLElement>}
          className={mergedClassName}
          render={renderElement}
          nativeButton={isNativeButton}
          {...props}
        />
      );
    }

    return (
      <BaseButton
        ref={ref as React.Ref<HTMLElement>}
        className={mergedClassName}
        {...props}
      >
        {children}
      </BaseButton>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
